<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ChannelHostingAPI from 'dashboard/api/channelHosting';
import {
  hostingConnectionOf,
  hostingToneDotClass,
  hostingStatusTooltipKey,
} from 'dashboard/helper/channelHostingStatus';
// FORK_PATCH_BEGIN: REQ-20260910-01 — Messenger history sync
import MessengerHistorySyncButton from 'custom/dashboard/extensions/messenger_history_sync/MessengerHistorySyncButton.vue';
// FORK_PATCH_END: REQ-20260910-01
// FORK_PATCH_BEGIN: REQ-20260910-02 — Messenger auth light
import MessengerReauthorizeButton from 'custom/dashboard/extensions/messenger_auth/MessengerReauthorizeButton.vue';
import {
  isMessengerInbox,
  messengerAuthDotClass,
  messengerAuthTooltipKey,
} from 'custom/dashboard/helper/messengerAuthStatus';
// FORK_PATCH_END: REQ-20260910-02

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');
// FORK_PATCH_BEGIN: inbox-category-nav
const selectedCategory = ref('all');
const hostingQuota = ref(null);
// FORK_PATCH_END: inbox-category-nav

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

// FORK_PATCH_BEGIN: inbox-category-helpers
const isHostingInbox = inbox => {
  const attrs = inbox.additional_attributes || {};
  return attrs.channel_hosting === true || attrs.channel_hosting_kind === 'linked_device';
};

const categoryOf = inbox => {
  if (isHostingInbox(inbox)) return 'whatsapp_hosting';
  if (inbox.provider === 'ycloud' || inbox.medium === 'whatsapp') {
    return 'whatsapp_api';
  }
  const type = inbox.channel_type || '';
  if (type.includes('Whatsapp')) return 'whatsapp_cloud';
  if (type.includes('WebWidget')) return 'website';
  if (type.includes('Email')) return 'email';
  if (type.includes('Api')) return 'api';
  if (type.includes('Telegram')) return 'telegram';
  if (type.includes('Line')) return 'line';
  if (type.includes('Facebook')) return 'facebook';
  if (type.includes('Instagram')) return 'instagram';
  if (type.includes('Tiktok')) return 'tiktok';
  if (type.includes('Sms') || type.includes('Twilio')) return 'sms';
  return 'other';
};

const categoryLabel = key => {
  const map = {
    all: t('CHANNEL_HOSTING.CATEGORY_ALL'),
    whatsapp_hosting: t('CHANNEL_HOSTING.CATEGORY_HOSTING'),
    whatsapp_cloud: t('CHANNEL_HOSTING.CATEGORY_CLOUD'),
    whatsapp_api: t('CHANNEL_HOSTING.CATEGORY_API'),
    website: 'Website',
    email: 'Email',
    api: 'API',
    telegram: 'Telegram',
    line: 'LINE',
    facebook: 'Messenger',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    sms: 'SMS',
    other: 'Other',
  };
  return map[key] || key;
};

const categories = computed(() => {
  const counts = {};
  inboxesList.value.forEach(inbox => {
    const key = categoryOf(inbox);
    counts[key] = (counts[key] || 0) + 1;
  });
  const keys = Object.keys(counts).sort();
  return [
    { key: 'all', count: inboxesList.value.length },
    ...keys.map(key => ({ key, count: counts[key] })),
  ];
});
// FORK_PATCH_END: inbox-category-helpers

const filteredInboxesList = computed(() => {
  let list = inboxesList.value;
  // FORK_PATCH_BEGIN: inbox-category-filter
  if (selectedCategory.value !== 'all') {
    list = list.filter(i => categoryOf(i) === selectedCategory.value);
  }
  // FORK_PATCH_END: inbox-category-filter
  const query = searchQuery.value.trim();
  if (!query) return list;
  return picoSearch(list, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    // FORK_PATCH_BEGIN: inbox-list-cache
    if (error?.alreadyDeleted) {
      useAlert(t('INBOX_MGMT.DELETE.API.ALREADY_DELETED'));
      return;
    }
    // FORK_PATCH_END: inbox-list-cache
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};

const loadHostingQuota = async () => {
  if (!isAdmin.value) return;
  try {
    const { data } = await ChannelHostingAPI.getQuota();
    hostingQuota.value = data;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug('[channel_hosting] list quota skipped', e);
  }
};

onMounted(loadHostingQuota);
watch(selectedCategory, key => {
  if (key === 'whatsapp_hosting') loadHostingQuota();
});
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <!-- FORK_PATCH_BEGIN: inbox-list-split-layout -->
      <div class="flex gap-0 border-t border-n-weak min-h-[28rem]">
        <aside
          class="w-52 shrink-0 border-r border-n-weak py-3 pr-2 flex flex-col gap-1"
        >
          <button
            v-for="cat in categories"
            :key="cat.key"
            type="button"
            class="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-left transition-colors"
            :class="
              selectedCategory === cat.key
                ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="selectedCategory = cat.key"
          >
            <span class="truncate">{{ categoryLabel(cat.key) }}</span>
            <span class="tabular-nums text-n-slate-10 text-xs">{{
              cat.count
            }}</span>
          </button>
        </aside>

        <div class="flex-1 min-w-0 pl-4">
          <div
            v-if="selectedCategory === 'whatsapp_hosting' && hostingQuota"
            class="mb-3 flex items-center justify-between rounded-xl border border-n-weak px-4 py-2 text-sm"
          >
            <span class="text-n-slate-11">{{
              $t('CHANNEL_HOSTING.QUOTA_LABEL')
            }}</span>
            <span class="font-medium tabular-nums text-n-slate-12"
              >{{ hostingQuota.used }} / {{ hostingQuota.limit }}</span
            >
          </div>

          <span
            v-if="!filteredInboxesList.length && searchQuery"
            class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
          >
            {{ $t('INBOX_MGMT.NO_RESULTS') }}
          </span>
          <div v-else class="divide-y divide-n-weak">
            <div
              v-for="inbox in filteredInboxesList"
              :key="inbox.id"
              class="flex justify-between flex-row items-center gap-4 py-4"
            >
              <div class="flex items-center gap-4 min-w-0">
                <div
                  v-if="inbox.avatar_url"
                  class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-content-center shrink-0"
                >
                  <Avatar
                    :src="inbox.avatar_url"
                    :name="inbox.name"
                    :size="24"
                    rounded-full
                  />
                </div>
                <div
                  v-else
                  class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-content-center shrink-0"
                >
                  <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
                </div>
                <div class="flex flex-col items-start gap-1 min-w-0">
                  <div class="flex items-center gap-2 min-w-0 max-w-full">
                    <span
                      class="block text-heading-3 text-n-slate-12 capitalize truncate"
                    >
                      {{ inbox.name }}
                    </span>
                    <!-- FORK_PATCH_BEGIN: channel-hosting-status-light -->
                    <span
                      v-if="hostingConnectionOf(inbox)"
                      v-tooltip.top="
                        $t(hostingStatusTooltipKey(hostingConnectionOf(inbox)))
                      "
                      class="size-2.5 rounded-full shrink-0"
                      :class="
                        hostingToneDotClass(hostingConnectionOf(inbox).tone)
                      "
                    />
                    <!-- FORK_PATCH_END: channel-hosting-status-light -->
                    <!-- FORK_PATCH_BEGIN: REQ-20260910-02 — Messenger auth light -->
                    <span
                      v-if="!hostingConnectionOf(inbox) && messengerAuthDotClass(inbox)"
                      v-tooltip.top="$t(messengerAuthTooltipKey(inbox))"
                      class="size-2.5 rounded-full shrink-0"
                      :class="messengerAuthDotClass(inbox)"
                    />
                    <!-- FORK_PATCH_END: REQ-20260910-02 -->
                  </div>
                  <span
                    v-if="inbox.provider === 'ycloud' && inbox.phone_number"
                    class="text-body-main text-n-slate-11"
                  >
                    {{ inbox.phone_number }}
                  </span>
                  <ChannelName
                    v-else
                    :channel-type="inbox.channel_type"
                    :medium="inbox.medium"
                    :voice-enabled="inbox.voice_enabled"
                    class="text-body-main text-n-slate-11"
                  />
                </div>
              </div>
              <div class="flex gap-3 justify-end shrink-0">
                <!-- FORK_PATCH_BEGIN: REQ-20260910-01 — Messenger history sync -->
                <MessengerHistorySyncButton
                  v-if="isAdmin && inbox.channel_type === 'Channel::FacebookPage'"
                  :inbox-id="inbox.id"
                  variant="list"
                />
                <!-- FORK_PATCH_END: REQ-20260910-01 -->
                <!-- FORK_PATCH_BEGIN: REQ-20260910-02 — list reauthorize -->
                <MessengerReauthorizeButton
                  v-if="
                    isAdmin &&
                    isMessengerInbox(inbox) &&
                    inbox.reauthorization_required
                  "
                  :inbox="inbox"
                />
                <!-- FORK_PATCH_END: REQ-20260910-02 -->
                <router-link
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                >
                  <Button
                    v-if="isAdmin"
                    v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                    icon="i-woot-settings"
                    slate
                    sm
                  />
                </router-link>
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-woot-bin"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  @click="openDelete(inbox)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- FORK_PATCH_END: inbox-list-split-layout -->
    </template>
  </SettingsLayout>

  <woot-confirm-delete-modal
    v-if="showDeletePopup"
    v-model:show="showDeletePopup"
    :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
    :message="confirmDeleteMessage"
    :confirm-text="deleteConfirmText"
    :reject-text="deleteRejectText"
    :confirm-value="selectedInbox.name"
    :confirm-place-holder-text="confirmPlaceHolderText"
    @on-confirm="confirmDeletion"
    @on-close="closeDelete"
  />
</template>
